"use strict"

import moment from "moment"

import DynamicPopupDialogTemplate from "./dynamic-popup-dialog.html"
import ConversionOverlay from "../../model/domain/conversion-overlay"
import Order from "../../model/domain/order"
import { OrderManager } from "../../model/domain/order-manager"
import OrderService from "../../services/order/order.service"
import OrganizationService from "../../services/organization/organization.service"
import AnalyticsService from "../../services/analytics/analytics.service"
import AuthService from "../../services/auth/auth.service"
import StaticContentService from "../../services/static/static-content.service"
import StorageService from "../../services/storage/storage.service"
import CacheService from "../../services/cache/cache.service"

/**
 * Controller for dynamic popup dialogs with conversion overlays
 */
export default class DynamicPopupController {
  /**
   * @ngInject
   * @param {OrderService} OrderService
   * @param {OrganizationService} OrganizationService
   * @param {AnalyticsService} AnalyticsService
   * @param {AuthService} AuthService
   * @param {StaticContentService} StaticContentService
   * @param {StorageService} StorageService
   * @param {CacheService} CacheService
   */
  constructor(
    $http,
    $q,
    $mdDialog,
    $location,
    $log,
    $scope,
    $window,
    OrderService,
    OrganizationService,
    AnalyticsService,
    AuthService,
    StaticContentService,
    StorageService,
    CacheService,
    user,
    organization,
  ) {
    // Angular services
    this.$http = $http
    this.$q = $q
    this.$mdDialog = $mdDialog
    this.$location = $location
    this.$log = $log
    this.$scope = $scope
    this.$window = $window

    // Services
    /** @type {AnalyticsService} */
    this._analyticsService = AnalyticsService
    /** @type {AuthService} */
    this._authService = AuthService
    /** @type {OrderService} */
    this._orderService = OrderService
    /** @type {OrganizationService} */
    this._organizationService = OrganizationService
    /** @type {StaticContentService} */
    this._staticContentService = StaticContentService
    /** @type {StorageService} */
    this._storageService = StorageService
    /** @type {CacheService} */
    this._cacheService = CacheService

    // Data models
    this._user = user
    this._organization = organization
    this._orderManager = undefined
    this.lastSeenOverlayPopup = this._storageService.lastSeenOverlayPopup

    // Component state
    this._initializeState()

    // Initialize conversion popup
    this._initializeConversionPopup()

    $scope.$on("$destroy", () => {
      this._handleDialogClose()
    })
  }

  /**
   * Initialize component state
   * @private
   */
  _initializeState() {
    this._timeRemaining = 0
    this._totalTime = 0
    this._daysElapsed = 0
    this._progressValue = 0
    this.dynamicPopupData = undefined
    this.activeOrder = undefined
  }

  /**
   * Initialize conversion popup functionality
   * @private
   */
  _initializeConversionPopup() {
    try {
      this._conversionPopupUtils = ConversionOverlay.create(this.$http)

      this._conversionPopupUtils
        .fetchPopUpConfig()
        .then((config) => this._handlePopupConfig(config))
        .catch((error) => {
          this.$log.error("Failed to fetch popup configuration:", error)
        })
    } catch (error) {
      this.$log.error("Failed to initialize conversion popup:", error)
    }
  }

  /**
   * Handle popup configuration
   * @param {Object} config - Popup configuration
   * @private
   */
  _handlePopupConfig(config) {
    if (!config || !this._isOrganizationAllowed(config)) {
      return
    }

    this.dynamicPopupData = config
    this._setupProgressBar()
    this._trackPopupShown()
  }

  _trackPopupShown() {
    this._analyticsService.sendEvent({
      eventTag: `${this.dynamicPopupData.metric}_shown`,
      properties: {
        organization: this._organization[0].id,
        user: this._user.email,
      },
    })
  }

  /**
   * Check if current organization is allowed to show popup
   * @param {Object} config - Popup configuration
   * @returns {boolean}
   * @private
   */
  _isOrganizationAllowed(config) {
    if (
      !this._organization ||
      !Array.isArray(this._organization) ||
      !config?.allowed_org_ids
    ) {
      return false
    }

    const orgIds = this._organization.map((org) => org?.id).filter(Boolean)
    return orgIds.some((id) => config.allowed_org_ids.includes(id))
  }

  /**
   * Setup progress bar based on date range
   * @private
   */
  _setupProgressBar() {
    if (!this.dynamicPopupData?.progress) {
      this.$log.warn("Progress configuration not available")
      return
    }

    const today = this._getCurrentDateString()
    const { min: startDate, max: endDate } = this.dynamicPopupData.progress

    this._timeRemaining = this._getDaysBetweenDates(today, endDate)
    this._totalTime = this._getDaysBetweenDates(startDate, endDate)
    this._daysElapsed = Math.max(0, this._totalTime - this._timeRemaining)

    // Calculate progress percentage (0-100)
    this._progressValue =
      this._totalTime > 0
        ? Math.max(
            0,
            Math.min(100, (this._daysElapsed / this._totalTime) * 100),
          )
        : 0
  }

  /**
   * Get current date as formatted string
   * @returns {string} Date in YYYY-MM-DD format
   * @private
   */
  _getCurrentDateString() {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, "0")
    const day = String(today.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  /**
   * Get all orders for current organizations
   * @returns {Promise<Array>} Promise resolving to flattened array of orders
   */
  getOrders() {
    if (!this._organization || !Array.isArray(this._organization)) {
      return this.$q.resolve([])
    }

    const orderPromises = this._organization.map((org) =>
      this._orderService.getForOrganization(org.id),
    )

    return this.$q
      .all(orderPromises)
      .then((results) => results.flat())
      .catch((error) => {
        this.$log.error("Failed to fetch orders:", error)
        return []
      })
  }

  /**
   * Handle conversion overlay popup CTA actions
   * @param {string} action - Action to perform
   */
  openConversionOverlayPopupCta(action) {
    this._trackCtaClick(action)

    switch (action) {
      case "ck:smartQuote":
        return this._handleSmartQuoteAction()

      case "ck:smartShareQuoteWithAdmin":
        return this._handleShareQuoteAction()

      default:
        return this._handleExternalLinkAction(action)
    }
  }

  _trackCtaClick(action) {
    this._analyticsService.sendEvent({
      eventTag: `${this.dynamicPopupData.metric}_clicked`,
      properties: {
        orgId: this._organization[0].id,
        userEmail: this._user.email,
        action: action,
      },
    })
  }

  /**
   * Handle smart quote action
   * @returns {Promise}
   * @private
   */
  _handleSmartQuoteAction() {
    return this.getOrders()
      .then((orders) => {
        this.activeOrder = Order.findActiveOrder(orders)

        if (this.activeOrder) {
          return this._staticContentService.goToOrder(this.activeOrder.id)
        } else {
          this.$log.warn("No active order found for smart quote action")
          return this.$q.resolve()
        }
      })
      .catch((error) => {
        this.$log.error("Failed to handle smart quote action:", error)
        return this.$q.reject(error)
      })
  }

  /**
   * Handle share quote with admin action
   * @returns {Promise}
   * @private
   */
  _handleShareQuoteAction() {
    return this.getOrders()
      .then((orders) => {
        this.cancel()

        this._orderManager = this._createOrderManager()

        if (this._organization?.[0]) {
          return this._orderManager.shareSchoolOrder(
            this._user,
            this._organization[0],
            orders,
          )
        }

        return this.$q.resolve()
      })
      .catch((error) => {
        this.$log.error("Failed to handle share quote action:", error)
        return this.$q.reject(error)
      })
  }

  /**
   * Create order manager instance
   * @returns {OrderManager}
   * @private
   */
  _createOrderManager() {
    return new OrderManager(
      this.$q,
      this.$log,
      undefined,
      this.$mdDialog,
      this.$location,
      this._authService,
      this._orderService,
      this._organizationService,
      undefined,
      this._cacheService,
      undefined,
    )
  }

  /**
   * Handle external link action
   * @param {string} url - URL to open
   * @returns {Promise}
   * @private
   */
  _handleExternalLinkAction(url) {
    try {
      if (typeof url === "string" && url.trim()) {
        this.$window.open(url, "_blank", "noreferrer")
      }
      return this.$q.resolve()
    } catch (error) {
      this.$log.error("Failed to open external link:", error)
      return this.$q.reject(error)
    }
  }

  /**
   * Calculate days between two dates
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {number} Number of days between dates
   * @private
   */
  _getDaysBetweenDates(startDate, endDate) {
    if (!startDate || !endDate) {
      return 0
    }

    const date1 = new Date(startDate)
    const date2 = new Date(endDate)

    // Validate dates
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      this.$log.warn("Invalid date format provided to getDaysBetweenDates")
      return 0
    }

    const timeDiff = Math.abs(date2.getTime() - date1.getTime())
    const daysDiff = Math.round(timeDiff / (1000 * 60 * 60 * 24))

    return daysDiff
  }

  // Getters for template access

  /**
   * @returns {number} Progress value (0-100)
   */
  get progressValue() {
    return this._progressValue
  }

  /**
   * @returns {number} Days remaining
   */
  get timeRemaining() {
    return this._timeRemaining
  }

  /**
   * @returns {string} Progress label from config
   */
  get progressLabel() {
    if (!this.dynamicPopupData?.progress?.label) {
      return "Progress"
    }
    return `${this.dynamicPopupData.progress.label}`
  }

  /**
   * @returns {string} Progress label with days remaining
   */
  get progressLabelDays() {
    const days = Math.floor(this._timeRemaining)
    const dayText = days === 1 ? "day" : "days"
    return `${days} ${dayText} to go`
  }

  /**
   * @returns {string} Organization name or fallback
   */
  get organizationName() {
    return this._organization?.[0]?.name || "your organization"
  }

  /**
   * Static method to show the dialog
   * @param {Object} $mdDialog - Angular Material dialog service
   * @param {Object} user - User object
   * @param {Object} organization - Organization object
   * @returns {Promise} Dialog promise
   */
  static show($mdDialog, user, organization) {
    return $mdDialog.show({
      controller: DynamicPopupController,
      template: DynamicPopupDialogTemplate,
      controllerAs: "ctrl",
      clickOutsideToClose: true,
      escapeToClose: true,
      locals: {
        user,
        organization,
      },
    })
  }

  _handleDialogClose() {
    this._storageService.lastSeenOverlayPopup = {
      showBanner: false,
      time: moment(),
    }
    this.lastSeenOverlayPopup = this._storageService.lastSeenOverlayPopup
  }

  /**
   * Cancel and close the dialog
   */
  cancel() {
    this.$mdDialog.cancel()
  }
}
